import { useState } from 'react'
import { toast } from '../../../shared/ui/toastStore'
import { confirm } from '../../../shared/ui/confirmStore'
import {
  COMPLAINT_KIND_LABELS,
  addSupplierComplaint,
  deleteSupplierComplaint,
  type SupplierCard,
  type SupplierComplaintKind,
} from '../../../shared/api/suppliers.api'

// Конверт ошибок API — { success: false, error: { message } } (русские тексты).
const errText = (e: unknown): string => {
  const data = (e as { response?: { data?: { error?: { message?: string }; message?: string } } })
    ?.response?.data
  return data?.error?.message ?? data?.message ?? (e as Error)?.message ?? 'Ошибка'
}

// Журнал рекламаций на странице карточки поставщика: форма «+ Случай»
// (по правам карточки от сервера) + список записей. Кнопка «×» — только у
// записей, которые сервер разрешит удалить (автор, РОЗ, админ). Вынесен из
// SupplierDetailPage (лимит 500 строк).
export function SupplierComplaintsSection({
  supplierId,
  supplier,
  canComplain,
  onChanged,
}: {
  supplierId: string
  supplier: SupplierCard['supplier']
  canComplain: boolean
  onChanged: () => void
}) {
  const [kind, setKind] = useState<SupplierComplaintKind>('QUALITY')
  const [description, setDescription] = useState('')
  const [requestNumber, setRequestNumber] = useState('')
  const [saving, setSaving] = useState(false)

  const totalComplaints = supplier.complaintsCount + supplier.complaints.length

  const submitComplaint = async () => {
    if (!description.trim() || saving) return
    setSaving(true)
    try {
      await addSupplierComplaint(supplierId, {
        kind,
        description: description.trim(),
        requestNumber: requestNumber.trim() || null,
      })
      setDescription('')
      setRequestNumber('')
      onChanged()
      toast.success('Случай записан в журнал рекламаций')
    } catch (e) {
      toast.error(errText(e))
    } finally {
      setSaving(false)
    }
  }

  const removeComplaint = async (complaintId: string) => {
    const ok = await confirm({
      title: 'Удалить запись журнала рекламаций?',
      confirmText: 'Удалить',
      danger: true,
    })
    if (!ok) return
    try {
      await deleteSupplierComplaint(complaintId)
      onChanged()
    } catch (e) {
      toast.error(errText(e))
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-2">
      <h3 className="text-sm font-semibold text-gray-900">
        Журнал рекламаций{' '}
        <span className="text-xs font-normal text-gray-500">
          (всего: {totalComplaints}
          {supplier.complaintsCount > 0 && `, из файла: ${supplier.complaintsCount}`})
        </span>
      </h3>
      {supplier.complaintsNotes && (
        <div className="text-xs text-gray-500">Заметка (из файла): {supplier.complaintsNotes}</div>
      )}
      {canComplain && (
        <div className="flex flex-wrap gap-2 items-start">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as SupplierComplaintKind)}
            className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
          >
            {Object.entries(COMPLAINT_KIND_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
          <input
            value={requestNumber}
            onChange={(e) => setRequestNumber(e.target.value)}
            placeholder="№ заявки (опц.)"
            className="w-28 px-2 py-1.5 border border-gray-300 rounded text-xs"
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void submitComplaint()}
            placeholder="Что случилось: поставил контрафакт, недогрузил 20 шт…"
            className="flex-1 min-w-[220px] px-2 py-1.5 border border-gray-300 rounded text-xs"
          />
          <button
            onClick={() => void submitComplaint()}
            disabled={!description.trim() || saving}
            className="px-3 py-1.5 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? '…' : '+ Случай'}
          </button>
        </div>
      )}
      {supplier.complaints.length === 0 ? (
        <div className="text-xs text-gray-400">Записей в журнале пока нет.</div>
      ) : (
        <ul className="space-y-1">
          {supplier.complaints.map((c) => (
            <li key={c.id} className="flex items-start gap-2 text-xs border-t border-gray-50 pt-1">
              <span className="shrink-0 px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                {COMPLAINT_KIND_LABELS[c.kind]}
              </span>
              <span className="flex-1 text-gray-700">
                {c.description}
                {c.requestNumber && (
                  <span className="text-gray-400"> · заявка {c.requestNumber}</span>
                )}
                {c.partNumber && <span className="text-gray-400"> · {c.partNumber}</span>}
              </span>
              <span className="shrink-0 text-gray-400">
                {new Date(c.createdAt).toLocaleDateString('ru-RU')} ·{' '}
                {c.createdBy?.name ?? c.createdBy?.email ?? '—'}
              </span>
              {c.canDelete && (
                <button
                  onClick={() => void removeComplaint(c.id)}
                  title="Удалить запись"
                  className="shrink-0 text-gray-300 hover:text-red-600"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
